import axios from 'axios';
import { config } from '../config';

const api = axios.create({
  baseURL: config.apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('accessToken');
      // Only redirect if not already on login page to avoid redirect loops
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    // Log 403 errors with more detail
    if (error.response?.status === 403) {
      console.error('403 Forbidden:', {
        url: error.config?.url,
        message: error.response?.data?.error || 'Insufficient permissions',
        userRole: error.response?.data?.userRole || 'unknown'
      });
    }
    return Promise.reject(error);
  }
);

// Assignment API functions
export const assignmentAPI = {
  getAllAssignments: () => api.get('/api/assignments'),
  getDeviceAssignments: (deviceId: string) => api.get(`/api/assignments/device/${deviceId}`),
  getResourceAssignments: (fileId: string) => api.get(`/api/assignments/resource/${fileId}`),
  createAssignment: (data: any) => api.post('/api/assignments', data),
  createBulkAssignments: (data: any) => api.post('/api/assignments/bulk', data),
  updateAssignment: (id: string, data: any) => api.put(`/api/assignments/${id}`, data),
  deleteAssignment: (id: string) => api.delete(`/api/assignments/${id}`),
  deleteBulkAssignments: (assignmentIds: string[]) => api.delete('/api/assignments/bulk', { data: { assignmentIds } }),
};

// File API functions
export const fileAPI = {
  scanRootDirectory: () => api.get('/api/files/scan'),
  scanSubdirectory: (path: string) => api.get(`/api/files/scan/${encodeURIComponent(path)}`),
  getAllFilesInFolder: (path: string) => api.get(`/api/files/files-in-folder/${encodeURIComponent(path)}`),
  createFolder: (data: any) => api.post('/api/files/folders', data),
  renameFolder: (data: any) => api.put('/api/files/folders/rename', data),
  deleteFolder: (data: any) => api.delete('/api/files/folders', { data }),
  moveFile: (data: any) => api.put('/api/files/move', data),
  copyFile: (data: any) => api.post('/api/files/copy', data),
  uploadFiles: (formData: FormData) => api.post('/api/files/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  downloadFile: (id: string) => api.get(`/api/files/${id}/download`),
  deleteFile: (id: string) => api.delete(`/api/files/${id}`),
  renameFile: (id: string, data: any) => api.put(`/api/files/${id}/rename`, data),
  reviseFile: (id: string, formData: FormData) => api.put(`/api/files/${id}/revise`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

// Device API functions
export const deviceAPI = {
  getAllDevices: () => api.get('/api/devices'),
  getDevice: (id: string) => api.get(`/api/devices/${id}`),
  createDevice: (data: any) => api.post('/api/devices', data),
  updateDevice: (id: string, data: any) => api.put(`/api/devices/${id}`, data),
  deleteDevice: (id: string) => api.delete(`/api/devices/${id}`),
  registerDevice: (data: any) => api.post('/api/devices/register', data),
  updateDeviceStatus: (id: string, status: string) => api.put(`/api/devices/${id}/status`, { status }),
  updateDeviceProject: (id: string, projectId: string | null) => api.put(`/api/devices/${id}/project`, { projectId }),
  updateDeviceLabel: (id: string, label: string | null) => api.put(`/api/devices/${id}/label`, { label }),
};

// Project API functions
export const projectAPI = {
  getAllProjects: () => api.get('/api/projects'),
  getProject: (id: string) => api.get(`/api/projects/${id}`),
  createProject: (data: any) => api.post('/api/projects', data),
  updateProject: (id: string, data: any) => api.put(`/api/projects/${id}`, data),
  deleteProject: (id: string) => api.delete(`/api/projects/${id}`),
};

export default api;
